import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetOne, useRefresh } from 'react-admin';
import type { Contact, Conversation, Ticket } from '../../../types/chat.types';

export interface TicketsPageState {
  /** Selected ticket ID from URL */
  selectedTicketId: number | null;
  /** Selected ticket data */
  selectedTicket: Ticket | null;
  /** Selected conversation ID (from ticket) */
  selectedConversationId: number | null;
  /** Selected conversation data */
  selectedConversation: Conversation | null;
  /** Whether ticket/conversation is loading */
  isLoadingConversation: boolean;
  /** Whether details panel is visible (for responsive) */
  isDetailsPanelOpen: boolean;
  /** Whether conversation list panel is visible (for mobile) */
  isConversationListOpen: boolean;
  /** Select a ticket and update URL */
  selectTicket: (ticketId: number | null) => void;
  /** Clear selection and refresh conversation list (use after leave/delete) */
  clearSelectionAndRefresh: () => void;
  /** Toggle details panel visibility */
  toggleDetailsPanel: () => void;
  /** Toggle conversation list panel visibility (mobile) */
  toggleConversationList: () => void;
  /** Close details panel */
  closeDetailsPanel: () => void;
  /** Open details panel */
  openDetailsPanel: () => void;
}

/**
 * Main hook for Tickets Page state management.
 * Manages URL-based ticket selection and responsive panel visibility.
 *
 * @returns Tickets page state and actions
 *
 * @example
 * ```tsx
 * function TicketsPage() {
 *   const {
 *     selectedTicketId,
 *     selectedTicket,
 *     selectedConversation,
 *     isDetailsPanelOpen,
 *     selectTicket,
 *     toggleDetailsPanel,
 *   } = useTicketsPage();
 *
 *   return (
 *     <Box display="flex">
 *       <ConversationListPanel
 *         selectedTicketId={selectedTicketId}
 *         onTicketSelect={(ticket) => selectTicket(ticket.id)}
 *       />
 *       <ChatPanel conversation={selectedConversation} />
 *       {isDetailsPanelOpen && <DetailsPanel ticket={selectedTicket} />}
 *     </Box>
 *   );
 * }
 * ```
 */
export function useTicketsPage(): TicketsPageState {
  const [searchParams, setSearchParams] = useSearchParams();
  const refresh = useRefresh();
  const ticketIdParam = searchParams.get('ticket');
  const ticketId = ticketIdParam ? Number(ticketIdParam) : null;

  // Panel visibility states (for responsive design)
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState<boolean>(false);
  const [isConversationListOpen, setIsConversationListOpen] = useState<boolean>(true);

  // Fetch selected ticket data
  const {
    data: selectedTicket,
    isLoading: isLoadingTicket,
  } = useGetOne<Ticket>(
    'ticket',
    { id: ticketId ?? '' },
    { enabled: !!ticketId }
  );

  // Get conversationId from ticket
  const conversationId = selectedTicket?.conversationId ?? null;

  // Fetch conversation data using conversationId from ticket
  const {
    data: rawConversation,
    isLoading: isLoadingConversation,
  } = useGetOne<Conversation>(
    'conversation',
    { id: conversationId ?? '' },
    { enabled: !!conversationId }
  );

  // Fetch full contact data to ensure avatarFileId is available
  const contactId = rawConversation?.contactId ?? null;
  const { data: fullContact } = useGetOne<Contact>(
    'contact',
    { id: contactId ?? '' },
    { enabled: !!contactId }
  );

  // Enrich conversation with full contact data (includes avatarFileId)
  const selectedConversation = rawConversation && fullContact
    ? { ...rawConversation, contact: { ...rawConversation.contact, ...fullContact } }
    : rawConversation ?? null;

  /**
   * Select a ticket and update URL params
   */
  const selectTicket = useCallback(
    (newTicketId: number | null) => {
      if (newTicketId) {
        setSearchParams({ ticket: String(newTicketId) });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams]
  );

  /**
   * Clear selection and refresh conversation list.
   * Use after leaving or deleting a conversation to avoid 403 errors.
   */
  const clearSelectionAndRefresh = useCallback(() => {
    // First clear the URL param to stop fetching the deleted/left conversation
    setSearchParams({});
    // Then refresh the list after a small delay to ensure URL is cleared
    setTimeout(() => {
      refresh();
    }, 100);
  }, [setSearchParams, refresh]);

  /**
   * Toggle details panel visibility
   */
  const toggleDetailsPanel = useCallback(() => {
    setIsDetailsPanelOpen((prev) => !prev);
  }, []);

  /**
   * Close details panel
   */
  const closeDetailsPanel = useCallback(() => {
    setIsDetailsPanelOpen(false);
  }, []);

  /**
   * Open details panel
   */
  const openDetailsPanel = useCallback(() => {
    setIsDetailsPanelOpen(true);
  }, []);

  /**
   * Toggle conversation list panel (for mobile)
   */
  const toggleConversationList = useCallback(() => {
    setIsConversationListOpen((prev) => !prev);
  }, []);

  // Auto-hide conversation list on mobile when a ticket is selected
  useEffect(() => {
    if (ticketId) {
      // On mobile screens, hide conversation list when a ticket is selected
      const isMobile = window.matchMedia('(max-width: 900px)').matches;
      if (isMobile) {
        setIsConversationListOpen(false);
      }
    }
  }, [ticketId]);

  return {
    selectedTicketId: ticketId,
    selectedTicket: selectedTicket ?? null,
    selectedConversationId: conversationId,
    selectedConversation: selectedConversation ?? null,
    isLoadingConversation: isLoadingTicket || isLoadingConversation,
    isDetailsPanelOpen,
    isConversationListOpen,
    selectTicket,
    clearSelectionAndRefresh,
    toggleDetailsPanel,
    toggleConversationList,
    closeDetailsPanel,
    openDetailsPanel,
  };
}
